import { qs, qsa } from './utils.js';
import { CFG } from './config.js';
import { loadPrices } from './api.js';

const getStateValue = (container) => qs(container, CFG.ids.stateDd)?.dataset.value?.toLowerCase() || '';
const getPatientValue = (container) => qs(container, CFG.ids.patientDd)?.dataset.value?.toLowerCase() || '';

const getLocationId = (container) => qs(container, CFG.ids.stateDd)?.dataset.id || '';

function parsePriceToCents (text) {
    if (!text) return null;
    const num = Number(text.replace(/[^0-9.]/g, ''));
    if (Number.isNaN(num)) return null;
    return Math.round(num * 100);
}

export function pickCurrentPrice (container) {
    const patient = getPatientValue(container);
    const state = getStateValue(container);
    const veteranCheck = qs(container, '#veteranCheck');
    const isVeteranChecked = !!(veteranCheck && veteranCheck.checked);

    // Визначаємо режим на основі типу пацієнта і статусу ветерана
    const mode = determineMode(patient, isVeteranChecked);

    console.log('Picking current price. Patient:', 
                patient, 'State:', 
                state, 'Veteran checked:', 
                isVeteranChecked, 
                'Mode:', mode);

    // Показуємо/ховаємо відповідні секції rates
    updateRatesSections(container, mode);

    // Знаходимо і показуємо відповідну ціну
    const pickedEl = findAndShowPrice(container, mode, state, patient);

    const priceText = pickedEl ? pickedEl.textContent.trim() : null;
    const priceCents = priceText ? parsePriceToCents(priceText) : null;
    return { priceText, priceCents, mode };
}

/**
 * Визначає режим показу цін на основі типу пацієнта
 */
function determineMode(patient, isVeteranChecked) {
    if (patient === 'new-patient' && isVeteranChecked) {
        return 'veteran';
    }
    if (patient === 'return-patient') {
        return 'renewal';
    }
    if (patient === 'patient-transfer') {
        return 'transfer';
    }
    return 'standard';
}

/**
 * Показує/ховає секції rates відповідно до режиму
 */
function updateRatesSections(container, mode) {
    console.log('Updating rates sections. Mode:', mode, 'Container:', container);

    const sections = {
        standard: '[data-default-rates]',
        veteran: '[data-veteran-rates]',
        renewal: '[data-renewal-rates]',
        transfer: '[data-transfer-rates]'
    };

    // Спочатку ховаємо всі секції
    Object.values(sections).forEach(selector => {
        qsa(container, selector).forEach(el => el.classList.add('d-none'));
    });

    // Потім показуємо тільки потрібну
    const activeSelector = sections[mode];
    if (activeSelector) {
        qsa(container, activeSelector).forEach(el => el.classList.remove('d-none'));
    }
}

/**
 * Знаходить і показує відповідну ціну
 */
function findAndShowPrice(container, mode, state, patient) {
    let pickedEl = null;

    qsa(container, CFG.selectors.priceBox).forEach(priceBox => {
        // Ховаємо всі варіанти цін
        hideAllPrices(priceBox);

        // Показуємо ціну відповідно до режиму
        const priceEl = selectPriceByMode(priceBox, mode, state, patient);
        if (priceEl) {
            priceEl.classList.add('is-visible');
            if (!pickedEl) pickedEl = priceEl;
        }
    });

    return pickedEl;
}

/**
 * Ховає всі варіанти цін у price box
 */
function hideAllPrices(priceBox) {
    const selectors = [
        CFG.selectors.priceStd,
        CFG.selectors.priceDefault,
        CFG.selectors.priceVeteran,
        CFG.selectors.priceRenewal,
        CFG.selectors.priceTransfer
    ].join(',');
    
    priceBox.querySelectorAll(selectors).forEach(el => el.classList.remove('is-visible'));
}

/**
 * Вибирає ціну відповідно до режиму
 */
function selectPriceByMode(priceBox, mode, state, patient) {
    // Спеціальні режими з окремими селекторами
    const specialModes = {
        veteran: CFG.selectors.priceVeteran,
        renewal: CFG.selectors.priceRenewal,
        transfer: CFG.selectors.priceTransfer
    };

    if (specialModes[mode]) {
        const specialPrice = priceBox.querySelector(specialModes[mode]);
        if (specialPrice) return specialPrice;
    }

    // Стандартний режим - шукаємо за state і patient
    const matchingPrice = findMatchingStandardPrice(priceBox, state, patient);
    if (matchingPrice) return matchingPrice;

    // Fallback на default ціну
    return priceBox.querySelector(CFG.selectors.priceDefault);
}

/**
 * Шукає стандартну ціну за state і patient
 */
function findMatchingStandardPrice(priceBox, state, patient) {
    const allStd = priceBox.querySelectorAll(CFG.selectors.priceStd);
    
    for (const opt of allStd) {
        const s = (opt.getAttribute('data-state') || '').toLowerCase();
        const p = (opt.getAttribute('data-patient') || '').toLowerCase();
        
        if (s === state && p === patient) {
            return opt;
        }
    }
    
    return null;
}

export function updateRatesView (container) {
    return reloadPricesFromBackend(container).then(() => {
        pickCurrentPrice(container);
    });
}

/**
 * Show/hide "Apply Coupon" links based on the veteran checkbox state.
 * Coupons are not applicable to veteran appointments.
 */
export function syncCouponVisibility (container) {
    const veteranCheck = qs(container, '#veteranCheck');
    const hide = !!(veteranCheck && veteranCheck.checked);
    qsa(container, '[data-bs-target="#coupon"]').forEach(link => {
        link.classList.toggle('d-none', hide);
    });
}

/**
 * Reload prices from backend via AJAX
 * Based on old appointment.js reloadPrices()
 */
export function reloadPricesFromBackend(container) {
    const locationSel = getLocationId(container);
    let patientSel = getPatientValue(container);
    const veteranCb = qs(container, '#veteranCheck');
    const grid = qs(container, '#servicesGrid');

    // Check veteran status: checkbox checked, input exists, has value, and no validation error
    const veteranIdInput = qs(container, '[name="veteran_id"]');
    const isVeteran = veteranCb?.checked && 
                      veteranIdInput && 
                      veteranIdInput.value.trim() && 
                      !veteranIdInput.classList.contains('error') ? 1 : 0;

    console.log('Reloading prices with params - Location:', locationSel, 'Patient type:', patientSel, 'isVeteran:', isVeteran);

    console.log('Veteran checkbox:', veteranCb, 'Veteran ID input:', veteranIdInput);
    console.log('error class:', veteranIdInput?.classList.contains('error'));


    // Clear value from -patient suffix: "new-patient" -> "new", "return-patient" -> "returning"
    if (patientSel === 'new-patient') {
        patientSel = 'new';
    } else if (patientSel === 'return-patient') {
        patientSel = 'returning';
    }
    
    //console.log('Reloading prices from backend...');
    //console.log('Location:', locationSel, 'Patient type:', patientSel, 'Veteran:', veteranCb?.checked);

    if (!grid) {
        console.warn('servicesGrid not found');
        return Promise.resolve();
    }

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const payload = {
        location: locationSel || '',
        patient_type: patientSel || '',
        veteran: isVeteran,
        tz: tz
    };

    grid.innerHTML = '<div class="col"><div class="text-center text-muted py-3">Loading prices…</div></div>';

    console.log('Price reload payload:', payload);

    return loadPrices(payload)
        .then(html => {
            grid.innerHTML = html;
            syncCouponVisibility(container);
            console.log('Prices updated from backend');
        })
        .catch(error => {
            console.error('Prices load failed', error);
            grid.innerHTML = '<div class="col"><div class="text-danger py-3">Failed to load prices.</div></div>';
        });
}

/**
 * Initialize dynamic price reloading with debounce
 */
// let priceReloadTimer = null;
// export function initDynamicPrices(container, debounceMs = 150) {
//     const locationSel = qs(container, CFG.ids.stateDd);
//     const patientSel = qs(container, CFG.ids.patientDd);
//     const veteranCb = qs(container, '#veteranCheck');

//     const debouncedReload = () => {
//         clearTimeout(priceReloadTimer);
//         priceReloadTimer = setTimeout(() => reloadPricesFromBackend(container), debounceMs);
//     };

//     locationSel?.addEventListener('change', debouncedReload);
//     patientSel?.addEventListener('change', debouncedReload);
//     veteranCb?.addEventListener('change', debouncedReload);
// }
